// Pure helpers for the food-preference draft shared by Settings and onboarding.
//
// A draft holds CANONICAL items only: { kind: 'group' | 'concept', id, label }
// plus legacy unresolved values ({ raw, label }) that can be kept or removed
// but never newly created. Every item comes from the backend (options/search);
// nothing here decides which ids are valid.

export var LISTS = ['allergens', 'dislikes'];

export function emptyDraft() {
  return { allergens: [], dislikes: [], unresolved: { allergens: [], dislikes: [] } };
}

export function itemKey(item) {
  return item ? item.kind + ':' + item.id : '';
}

function unresolvedKey(u) {
  return String((u && u.raw) || '').trim().toLowerCase();
}

function cloneItem(item) {
  var out = { kind: item.kind, id: item.id, label: item.label || item.id };
  if (item.status) out.status = item.status;
  if (item.covers) out.covers = item.covers;
  return out;
}

/** Draft from a GET/PUT /profile/food-preferences payload. */
export function draftFromPayload(payload) {
  var p = payload || {};
  var un = p.unresolved || {};
  return {
    allergens: (p.allergens || []).map(cloneItem),
    dislikes: (p.dislikes || []).map(cloneItem),
    unresolved: {
      allergens: (un.allergens || []).map(function (u) { return { raw: u.raw, label: u.label || u.raw }; }),
      dislikes: (un.dislikes || []).map(function (u) { return { raw: u.raw, label: u.label || u.raw }; }),
    },
  };
}

export function addItem(draft, list, item) {
  var cur = draft[list] || [];
  if (cur.some(function (i) { return itemKey(i) === itemKey(item); })) return draft;
  var next = Object.assign({}, draft);
  next[list] = cur.concat([cloneItem(item)]);
  return next;
}

export function removeItem(draft, list, key) {
  var next = Object.assign({}, draft);
  next[list] = (draft[list] || []).filter(function (i) { return itemKey(i) !== key; });
  return next;
}

export function removeUnresolved(draft, list, raw) {
  var un = Object.assign({ allergens: [], dislikes: [] }, draft.unresolved);
  un[list] = (un[list] || []).filter(function (u) { return unresolvedKey(u) !== unresolvedKey({ raw: raw }); });
  return Object.assign({}, draft, { unresolved: un });
}

/** What the user changed relative to the saved baseline. */
export function diffDraft(baseline, draft) {
  var diff = { added: {}, removed: {}, removedUnresolved: {} };
  LISTS.forEach(function (list) {
    var baseKeys = {};
    (baseline[list] || []).forEach(function (i) { baseKeys[itemKey(i)] = i; });
    var draftKeys = {};
    (draft[list] || []).forEach(function (i) { draftKeys[itemKey(i)] = i; });
    diff.added[list] = (draft[list] || []).filter(function (i) { return !baseKeys[itemKey(i)]; });
    diff.removed[list] = (baseline[list] || []).filter(function (i) { return !draftKeys[itemKey(i)]; });
    var draftUn = {};
    ((draft.unresolved || {})[list] || []).forEach(function (u) { draftUn[unresolvedKey(u)] = true; });
    diff.removedUnresolved[list] = ((baseline.unresolved || {})[list] || []).filter(function (u) {
      return !draftUn[unresolvedKey(u)];
    });
  });
  return diff;
}

export function countChanges(diff) {
  var n = 0;
  LISTS.forEach(function (list) {
    n += diff.added[list].length + diff.removed[list].length + diff.removedUnresolved[list].length;
  });
  return n;
}

export function isDirty(baseline, draft) {
  if (!baseline || !draft) return false;
  return countChanges(diffDraft(baseline, draft)) > 0;
}

/** Re-apply a pending diff on top of a newer server baseline (409 reconcile). */
export function applyDiff(baseline, diff) {
  var next = draftFromPayload(baseline);
  LISTS.forEach(function (list) {
    diff.removed[list].forEach(function (i) { next = removeItem(next, list, itemKey(i)); });
    diff.added[list].forEach(function (i) { next = addItem(next, list, i); });
    diff.removedUnresolved[list].forEach(function (u) { next = removeUnresolved(next, list, u.raw); });
  });
  return next;
}

function refs(items) {
  return (items || []).map(function (i) { return { kind: i.kind, id: i.id }; });
}

/** PUT /profile/food-preferences body. */
export function toSaveBody(draft, revision) {
  var un = draft.unresolved || {};
  return {
    expected_revision: revision || 0,
    allergens: refs(draft.allergens),
    dislikes: refs(draft.dislikes),
    keep_unresolved: {
      allergens: (un.allergens || []).map(function (u) { return u.raw; }),
      dislikes: (un.dislikes || []).map(function (u) { return u.raw; }),
    },
  };
}

/** Structured onboarding field (same shape as the Settings save). */
export function toOnboardingPreferences(draft) {
  var body = toSaveBody(draft, 0);
  return { allergens: body.allergens, dislikes: body.dislikes, keep_unresolved: body.keep_unresolved };
}

/** Legacy string arrays (ids + kept unresolved values), sent alongside the
 *  structured field so an older backend still receives the preferences. */
export function legacyArrays(draft) {
  var out = {};
  LISTS.forEach(function (list) {
    var ids = (draft[list] || []).map(function (i) { return i.id; });
    ((draft.unresolved || {})[list] || []).forEach(function (u) {
      if (ids.indexOf(u.raw) === -1) ids.push(u.raw);
    });
    out[list] = ids;
  });
  return out;
}

/** Store profile after a successful save: the server's structured doc plus
 *  the legacy mirrors, merged over (never replacing) the rest of the profile. */
export function mergeProfilePatch(profile, patch) {
  var p = patch || {};
  var next = Object.assign({}, profile || {});
  ['food_preferences', 'allergens', 'allergies', 'dislikes'].forEach(function (k) {
    if (p[k] !== undefined) next[k] = p[k];
  });
  return next;
}

function prettify(raw) {
  return String(raw || '').replace(/_/g, ' ');
}

/**
 * Onboarding restore. Order: the in-progress onboarding draft, then a stored
 * structured preference doc, then legacy arrays (a legacy string becomes a
 * group only when the BACKEND options list it; anything else is an
 * unrecognized value the user can remove).
 */
export function restoreOnboardingDraft(profile, groups) {
  var p = profile || {};
  if (p.food_preferences_draft) {
    var d = p.food_preferences_draft;
    return {
      allergens: (d.allergens || []).map(cloneItem),
      dislikes: (d.dislikes || []).map(cloneItem),
      unresolved: {
        allergens: ((d.unresolved || {}).allergens || []).slice(),
        dislikes: ((d.unresolved || {}).dislikes || []).slice(),
      },
    };
  }
  var fp = p.food_preferences;
  if (fp && fp.schema_version === 2) {
    return draftFromPayload({
      allergens: (fp.allergens || []).filter(function (i) { return i.kind === 'group' || i.kind === 'concept'; }),
      dislikes: (fp.dislikes || []).filter(function (i) { return i.kind === 'group' || i.kind === 'concept'; }),
      unresolved: fp.unresolved,
    });
  }
  var byId = {};
  (groups || []).forEach(function (g) { byId[g.id] = g; });
  var draft = emptyDraft();
  var allergens = Array.isArray(p.allergens) ? p.allergens : (Array.isArray(p.allergies) ? p.allergies : []);
  var dislikes = Array.isArray(p.dislikes) ? p.dislikes : [];
  [['allergens', allergens], ['dislikes', dislikes]].forEach(function (pair) {
    var list = pair[0];
    pair[1].forEach(function (raw) {
      if (typeof raw !== 'string' || !raw.trim()) return;
      var id = raw.trim().toLowerCase().replace(/[\s-]+/g, '_');
      if (byId[id]) {
        draft = addItem(draft, list, { kind: 'group', id: id, label: byId[id].label });
      } else if (!draft.unresolved[list].some(function (u) { return unresolvedKey(u) === unresolvedKey({ raw: raw }); })) {
        draft.unresolved[list] = draft.unresolved[list].concat([{ raw: raw, label: prettify(raw) }]);
      }
    });
  });
  return draft;
}
